import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertTriangle,
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Download,
  ImageIcon,
  MapPin,
  MessageSquare,
  User,
  CheckCircle,
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useIncident } from '@/api/hooks/useIncidents';
import { useAssetIncidents, AssetIncidentEntry } from '@/api/hooks/useAssets';

// --- Shared helpers --------------------------------------------------------

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '-';
  try {
    return format(new Date(dateString), "d 'de' MMM yyyy, HH:mm", { locale: es });
  } catch {
    return dateString;
  }
}

// --- Image Lightbox --------------------------------------------------------

function ImageLightbox({
  images,
  selectedIndex,
  onClose,
  onPrev,
  onNext,
}: {
  images: { id: number; url: string; fileName: string }[];
  selectedIndex: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}) {
  const { t } = useTranslation('common');
  const image = images[selectedIndex];

  const handleDownload = async () => {
    try {
      const response = await fetch(image.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = image.fileName || 'incident-image.jpg';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="max-w-[90vw] max-h-[90vh] p-0 overflow-hidden">
        <DialogTitle className="sr-only">{image?.fileName || 'Incident Image'}</DialogTitle>
        <div className="relative flex items-center justify-center bg-black">
          <img
            src={image?.url}
            alt={image?.fileName || 'Incident image'}
            className="max-h-[85vh] max-w-full object-contain"
          />
          {images.length > 1 && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 text-white hover:bg-black/70"
                onClick={onPrev}
              >
                <ChevronLeft className="h-6 w-6" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 text-white hover:bg-black/70"
                onClick={onNext}
              >
                <ChevronRight className="h-6 w-6" />
              </Button>
            </>
          )}
          <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-white">
            <span className="text-sm">
              {selectedIndex + 1} / {images.length}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/20"
              onClick={handleDownload}
            >
              <Download className="mr-2 h-4 w-4" />
              {t('buttons.download', 'Descargar')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// --- Image thumbnails for an incident --------------------------------------

function IncidentImages({ images }: { images: { id: number; url: string; fileName: string }[] }) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  if (images.length === 0) return null;

  return (
    <div className="mt-3">
      <div className="flex gap-2 overflow-x-auto pb-1">
        {images.map((img, idx) => (
          <button
            key={img.id}
            type="button"
            onClick={() => setLightboxIndex(idx)}
            className="relative h-20 w-20 flex-shrink-0 overflow-hidden rounded-md bg-muted ring-1 ring-border transition-all hover:ring-2 hover:ring-primary"
          >
            <img src={img.url} alt={img.fileName} className="h-full w-full object-cover" />
          </button>
        ))}
      </div>
      <span className="text-xs text-muted-foreground mt-1 block">
        {images.length} {images.length === 1 ? 'imagen' : 'imagenes'}
      </span>

      {lightboxIndex !== null && (
        <ImageLightbox
          images={images}
          selectedIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onPrev={() =>
            setLightboxIndex((prev) =>
              prev !== null && prev > 0 ? prev - 1 : images.length - 1,
            )
          }
          onNext={() =>
            setLightboxIndex((prev) =>
              prev !== null && prev < images.length - 1 ? prev + 1 : 0,
            )
          }
        />
      )}
    </div>
  );
}

// --- Single incident card with lazy-loaded detail --------------------------

function IncidentCard({ entry, defaultExpanded }: { entry: AssetIncidentEntry; defaultExpanded?: boolean }) {
  const [expanded, setExpanded] = useState(defaultExpanded ?? false);
  const { data: detail, isLoading } = useIncident(entry.id, expanded);

  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3 min-w-0">
          <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0" />
          <div className="min-w-0">
            <span className="font-medium text-sm">
              {entry.type}
            </span>
            <span className="text-xs text-muted-foreground ml-2">
              #{entry.id}
            </span>
          </div>
          {entry.resolved ? (
            <Badge className="bg-green-100 text-green-700 hover:bg-green-100 ml-2">
              <CheckCircle className="h-3 w-3 mr-1" />
              Resuelto
            </Badge>
          ) : (
            <Badge variant="destructive" className="ml-2">Abierto</Badge>
          )}
        </div>
        {expanded ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        )}
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t bg-muted/30 space-y-3">
          {isLoading ? (
            <div className="space-y-2 pt-3">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-4 w-64" />
              <div className="flex gap-2">
                <Skeleton className="h-20 w-20" />
                <Skeleton className="h-20 w-20" />
              </div>
            </div>
          ) : (
            <div className="pt-3 space-y-2">
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <Calendar className="h-3.5 w-3.5" />
                  {formatDate(entry.date)}
                </div>
                {detail?.locationName && (
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <MapPin className="h-3.5 w-3.5" />
                    {detail.locationName}
                  </div>
                )}
                {detail?.createdBy && (
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <User className="h-3.5 w-3.5" />
                    {detail.createdBy}
                  </div>
                )}
              </div>

              {entry.notes && (
                <div className="flex items-start gap-1.5 text-sm">
                  <MessageSquare className="h-3.5 w-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <p className="text-muted-foreground">{entry.notes}</p>
                </div>
              )}

              {entry.resolved && entry.resolvedDate && (
                <div className="text-xs text-green-700 bg-green-50 rounded px-2 py-1">
                  Resuelto el {formatDate(entry.resolvedDate)}
                </div>
              )}

              {detail && detail.images.length > 0 && (
                <IncidentImages images={detail.images} />
              )}

              {detail && detail.images.length === 0 && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground pt-1">
                  <ImageIcon className="h-3 w-3" />
                  Sin imagenes
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// --- Single incident detail (used by incidents page) -----------------------

function SingleIncidentDetail({ incidentId }: { incidentId: number }) {
  const { data: incident, isLoading, error } = useIncident(incidentId);

  if (isLoading) {
    return (
      <div className="space-y-4 py-4">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-4 w-64" />
        <Skeleton className="h-4 w-48" />
        <div className="flex gap-2">
          <Skeleton className="h-20 w-20" />
          <Skeleton className="h-20 w-20" />
        </div>
      </div>
    );
  }

  if (error || !incident) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Error al cargar los detalles del incidente
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Type & status row */}
      <div className="flex items-center gap-3">
        <AlertTriangle className="h-5 w-5 text-red-500" />
        <span className="font-semibold">{incident.typeName}</span>
        {incident.status === 'Resolved' ? (
          <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
            <CheckCircle className="h-3 w-3 mr-1" />
            Resuelto
          </Badge>
        ) : (
          <Badge variant="destructive">Abierto</Badge>
        )}
      </div>

      {/* Detail grid */}
      <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm border rounded-lg p-4 bg-muted/30">
        <div>
          <span className="text-muted-foreground block text-xs font-medium uppercase tracking-wide mb-0.5">
            Activo
          </span>
          <span className="font-medium">{incident.assetCode}</span>
        </div>
        <div>
          <span className="text-muted-foreground block text-xs font-medium uppercase tracking-wide mb-0.5">
            Fecha
          </span>
          {formatDate(incident.createdAt)}
        </div>
        {incident.locationName && (
          <div>
            <span className="text-muted-foreground block text-xs font-medium uppercase tracking-wide mb-0.5">
              Ubicacion
            </span>
            {incident.locationName}
          </div>
        )}
        <div>
          <span className="text-muted-foreground block text-xs font-medium uppercase tracking-wide mb-0.5">
            Reportado por
          </span>
          {incident.createdBy}
        </div>
      </div>

      {/* Notes */}
      {incident.description && (
        <div className="text-sm">
          <span className="text-muted-foreground block text-xs font-medium uppercase tracking-wide mb-1">
            Notas
          </span>
          <p className="text-foreground bg-muted/30 border rounded-lg p-3">
            {incident.description}
          </p>
        </div>
      )}

      {/* Resolution */}
      {incident.status === 'Resolved' && incident.resolvedAt && (
        <div className="text-sm bg-green-50 border border-green-200 rounded-lg p-3">
          <span className="text-green-800 font-medium">Resuelto</span>
          <span className="text-green-700 ml-2">el {formatDate(incident.resolvedAt)}</span>
        </div>
      )}

      {/* Images */}
      {incident.images.length > 0 ? (
        <div>
          <span className="text-muted-foreground block text-xs font-medium uppercase tracking-wide mb-2">
            Imagenes ({incident.images.length})
          </span>
          <IncidentImages images={incident.images} />
        </div>
      ) : (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <ImageIcon className="h-4 w-4" />
          Sin imagenes adjuntas
        </div>
      )}
    </div>
  );
}

// --- Asset incidents list (used by inventory page) -------------------------

function AssetIncidentsList({ assetId }: { assetId: string }) {
  const { data: incidents, isLoading, error } = useAssetIncidents(assetId);

  if (isLoading) {
    return (
      <div className="space-y-3 py-2">
        {[1, 2].map((i) => (
          <div key={i} className="border rounded-lg p-4 space-y-2">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-4 w-64" />
          </div>
        ))}
      </div>
    );
  }

  if (error || !incidents) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Error al cargar los incidentes
      </div>
    );
  }

  if (incidents.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No se encontraron incidentes para este activo
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {incidents.map((entry, idx) => (
        <IncidentCard key={entry.id} entry={entry} defaultExpanded={idx === 0} />
      ))}
    </div>
  );
}

// --- Main dialog component -------------------------------------------------

interface IncidentDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface SingleMode extends IncidentDetailDialogProps {
  mode: 'single';
  incidentId: number;
  assetId?: never;
  assetName?: never;
}

interface AssetMode extends IncidentDetailDialogProps {
  mode: 'asset';
  assetId: string;
  assetName?: string;
  incidentId?: never;
}

export type IncidentDetailDialogAllProps = SingleMode | AssetMode;

export function IncidentDetailDialog(props: IncidentDetailDialogAllProps) {
  const { open, onOpenChange, mode } = props;

  const title =
    mode === 'single'
      ? `Incidente #${props.incidentId}`
      : `Incidentes del Activo`;

  const subtitle =
    mode === 'asset' && props.assetName
      ? props.assetName
      : mode === 'asset'
        ? props.assetId
        : undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            {title}
          </DialogTitle>
          {subtitle && <DialogDescription>{subtitle}</DialogDescription>}
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-4">
          {mode === 'single' ? (
            <SingleIncidentDetail incidentId={props.incidentId} />
          ) : (
            <AssetIncidentsList assetId={props.assetId} />
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
